import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'antd';
import { geekblue } from '@ant-design/colors';
import moment from 'moment';
import { Link } from 'react-router-dom';
import RestHelper from 'utils/RestHelper';
import { ze } from 'utils/zhEn';
import { ExceptionCategory, ExceptionCategoryUtil } from 'utils/exception-category';
import { getIssueDetailUrlPrefix } from 'utils/helper';
import { QueryAccessUtil } from 'utils/crash-search/query-access-util';
import { TimeZoneUtil } from 'utils/time-zone-util';
import {
  formatPredecessorDelta,
  parseDateTimeMillis,
  selectSameLaunchPredecessors,
} from './sameLaunchSessionHint';

function invalidId(value) {
  return !value || String(value).toLowerCase() === 'unknown';
}

function canonicalCategory(csType) {
  if (ExceptionCategoryUtil.isAnr(csType)) return 'ANR';
  if (ExceptionCategoryUtil.isCrash(csType)) return 'CRASH';
  if (ExceptionCategoryUtil.isOom(csType)) return 'OOM';
  if (ExceptionCategoryUtil.isJank(csType)) return 'JANK';
  if (ExceptionCategoryUtil.isHang && ExceptionCategoryUtil.isHang(csType)) return 'HANG';
  if (ExceptionCategoryUtil.isError(csType)) return 'ERROR';
  return 'OTHER';
}

function categoryLabel(csType) {
  if (csType === 'ERROR') return 'Error';
  if (csType === 'ANR') return 'ANR';
  if (csType === 'CRASH') return ze('崩溃', 'Crash');
  if (csType === 'OOM') return 'OOM';
  if (csType === 'JANK') return ze('卡顿', 'Jank');
  if (csType === 'HANG') return ze('卡死', 'Hang');
  return csType;
}

function categoryColor(csType) {
  if (csType === 'ERROR') return '#A452DD';
  if (csType === 'ANR') return '#FB835E';
  if (csType === 'CRASH') return '#FF5461';
  return geekblue[6];
}

function oneLine(text) {
  const line = String(text || '').split('\n')[0].trim();
  if (!line) return '';
  return line.length > 80 ? `${line.slice(0, 80)}…` : line;
}

function normalizeRecord(record, utcOffset) {
  const launchTimeMillis = record.launchTimeMillisStr ? Number(record.launchTimeMillisStr) : 0;
  let uploadText = record.uploadTime;
  try {
    uploadText = TimeZoneUtil.convertDefaultTimeZoneLocalDateTimeToUtcOffsetMoment(record.uploadTime, utcOffset)
      .format('YYYY-MM-DD HH:mm:ss');
  } catch (e) {
    uploadText = record.uploadTime;
  }
  return {
    crashId: record.crashId,
    issueId: record.issueId,
    csType: canonicalCategory(record.csType),
    expMessage: record.expMessage,
    expUid: record.expUid,
    launchTimeSeconds: launchTimeMillis ? Math.floor(launchTimeMillis / 1000) : 0,
    uploadTimeMillis: parseDateTimeMillis(uploadText),
  };
}

export default function SameLaunchSessionHint({
  appId,
  platformId,
  crashId,
  deviceId,
  userId,
  utcOffset,
  onOpenSameSession,
}) {
  const [selection, setSelection] = useState(null);
  const deviceMissing = invalidId(deviceId);
  const userMissing = invalidId(userId);

  useEffect(() => {
    if (!appId || !crashId || (deviceMissing && userMissing)) {
      setSelection(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const params = {
          uploadTimeBeginMillis: moment().subtract(QueryAccessUtil.SUPPORTED_DAYS, 'day').valueOf(),
          appId,
          platformId,
          skipDistinctQuery: true,
          pageNumber: 1,
          pageSize: 1000,
          exceptionCategoryList: [
            ExceptionCategory.CRASH,
            ExceptionCategory.ANR,
            ExceptionCategory.ERROR,
            ExceptionCategory.OOM,
            ExceptionCategory.JANK,
            ExceptionCategory.HANG,
          ].filter(Boolean),
        };
        if (!deviceMissing) {
          params.deviceIdList = [deviceId];
        } else {
          params.userIdList = [userId];
        }
        const rsp = await RestHelper.mustPost('/redir/api/queryAccess/queryAccessList', params, {
          silentFailure: true,
        });
        if (cancelled) return;
        const records = (((rsp.json || {}).data || {}).result || {}).records || [];
        const next = selectSameLaunchPredecessors(
          records.map(item => normalizeRecord(item, utcOffset)),
          crashId,
        );
        setSelection(next.total > 0 ? next : null);
      } catch (e) {
        if (!cancelled) setSelection(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [appId, platformId, crashId, deviceId, userId, utcOffset, deviceMissing, userMissing]);

  if (!selection) {
    return null;
  }

  const countBits = [];
  if (selection.errorCount) countBits.push(`Error ${selection.errorCount} 条`);
  if (selection.anrCount) countBits.push(`ANR ${selection.anrCount} 条`);
  const nearest = formatPredecessorDelta(selection.nearestDeltaMs);
  const detail = countBits.length
    ? `（${countBits.join('，')}，最近一条距崩溃 ${nearest}）`
    : `（最近一条距崩溃 ${nearest}）`;

  return (
    <div
      data-testid="same-launch-session-hint"
      style={{
        marginBottom: 12,
        padding: '8px 12px 10px',
        background: geekblue[0],
        border: `1px solid ${geekblue[2]}`,
        borderRadius: 4,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: geekblue[7] }}>
          {ze('同设备本次启动会话内，崩溃前还上报了 ', 'Before this crash in the same launch: ')}
          {selection.total}
          {ze(' 条异常', ' reports')}
          <span style={{ fontWeight: 400, color: '#393157' }}>{detail}</span>
        </div>
        <Button
          size="small"
          data-testid="same-launch-session-open"
          onClick={onOpenSameSession}
        >
          {ze('查看完整同设备上报序列 →', 'View same-device sequence →')}
        </Button>
      </div>
      <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
        {selection.items.map(item => {
          const summary = oneLine(item.expMessage);
          let path = '';
          if (item.csType === 'OOM' && item.expUid) {
            path = `/crash-reporting/oom/${appId}?pid=${platformId}&expUid=${item.expUid}`;
          } else if (item.issueId && item.crashId) {
            path = `/${getIssueDetailUrlPrefix(item.csType)}/${appId}/${item.issueId}/report-id/${item.crashId}?pid=${platformId}`;
          }
          return (
            <div
              key={item.crashId}
              style={{
                display: 'grid',
                gridTemplateColumns: '64px 128px 1fr auto',
                gap: 8,
                alignItems: 'center',
                fontSize: 12,
                background: '#fff',
                borderRadius: 2,
                padding: '4px 8px',
              }}
            >
              <span style={{ color: categoryColor(item.csType), fontWeight: 600 }}>{categoryLabel(item.csType)}</span>
              <span style={{ color: '#807997' }}>
                {ze('崩溃前 ', 'T-')}
                {formatPredecessorDelta(item.deltaMs)}
              </span>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {summary || item.issueId || item.crashId}
              </span>
              {path ? (
                <Link to={path} target="_blank" rel="noopener noreferrer">{item.issueId}</Link>
              ) : (
                <span>{item.crashId}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

SameLaunchSessionHint.propTypes = {
  appId: PropTypes.string,
  platformId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  crashId: PropTypes.string,
  deviceId: PropTypes.string,
  userId: PropTypes.string,
  utcOffset: PropTypes.number,
  onOpenSameSession: PropTypes.func,
};
